import type { RendererProps } from "@circle-react-uikit/ImageInput/ImageSelector";
import type { IconName } from "@circleco/compass/components/Icon";
import type { ReactNode } from "react";

import { t } from "@/i18n-js/instance";

import { CompassLikeImageInputEmptyState } from "./CompassLikeImageInputEmptyState";
import { CompassLikeImageInputLoader } from "./CompassLikeImageInputLoader";
import { CompassLikeImageInputOverlay } from "./CompassLikeImageInputOverlay";

export interface CompassLikeImageInputRendererOptions {
  emptyStateDescription: ReactNode;
  emptyStateIconName?: IconName;
  emptyStateTitle?: ReactNode;
  previewAlt?: string;
  processingLabel?: ReactNode;
}

export interface CompassLikeImageInputRendererProps
  extends RendererProps, CompassLikeImageInputRendererOptions {}

export const CompassLikeImageInputRenderer = ({
  changeImageText,
  emptyStateDescription,
  emptyStateIconName,
  emptyStateTitle,
  handleChangeCoverClick: handleChangeImageClick,
  handleDelete,
  imageUrl,
  previewAlt,
  processingLabel,
  processing: isProcessing,
  value,
}: CompassLikeImageInputRendererProps) => {
  const hasImage = Boolean(value);

  if (!hasImage && !isProcessing) {
    return (
      <button
        type="button"
        aria-label={t("add_image")}
        className="bg-primary border-primary hover:bg-hover hover:border-hover active:bg-active active:border-active group flex size-full min-h-30 cursor-pointer select-none items-center gap-4 rounded-lg border-2 border-dashed p-6 text-left transition-colors duration-200"
        onClick={handleChangeImageClick}
      >
        <CompassLikeImageInputEmptyState
          emptyStateDescription={emptyStateDescription}
          emptyStateIconName={emptyStateIconName}
          emptyStateTitle={emptyStateTitle}
        />
      </button>
    );
  }

  return (
    <div className="relative h-full">
      {isProcessing && <CompassLikeImageInputLoader label={processingLabel} />}
      {hasImage && imageUrl && !isProcessing && (
        <>
          <img
            loading="lazy"
            className="size-full rounded-md object-cover"
            src={imageUrl}
            alt={previewAlt ?? ""}
          />
          <CompassLikeImageInputOverlay
            onDelete={handleDelete}
            onChange={handleChangeImageClick}
            removeButtonText={t("remove")}
            changeButtonText={changeImageText ?? t("replace")}
          />
        </>
      )}
    </div>
  );
};
